import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Прокрутка экрана квиза — две настройки повтора эталона Slimkit:
 *  - сброс прокрутки в 0 при смене экрана (пак `states.resetScrollOnEnter`,
 *    spec shared/screen-container.md «Что добавить» п.7; эталон
 *    `pages/index.tsx:40`);
 *  - признак «экран прокручивается» для подложки прибитой кнопки
 *    `solid-when-scroll` (cta-button.md «Фон обычной обёртки»; эталон считает
 *    его при каждой смене шага, `pages/index.tsx:35-65`).
 *
 * Прокручивается либо окно (публичная страница), либо ближайший предок с
 * `overflow-y: auto|scroll` (канва конструктора) — ищем его от корня экрана
 * `[data-screen-id]`, без ref в большом файле визарда.
 */

function isScrollableBox(el: Element): boolean {
  const { overflowY } = getComputedStyle(el);
  return overflowY === 'auto' || overflowY === 'scroll';
}

/** Ближайший прокручиваемый предок; null — прокручивается окно. */
export function findScrollParent(start: Element | null): HTMLElement | null {
  let el = start?.parentElement ?? null;
  while (el && el !== document.body && el !== document.documentElement) {
    if (isScrollableBox(el)) return el;
    el = el.parentElement;
  }
  return null;
}

function screenElement(screenId: string): Element | null {
  if (typeof document === 'undefined') return null;
  // CSS.escape нет в jsdom; в значении атрибута в кавычках опасны только `"` и `\`.
  const safe = screenId.replace(/["\\]/g, '\\$&');
  return document.querySelector(`[data-screen-id="${safe}"]`);
}

/** Прокрутить в 0 контейнер экрана (или окно), без плавности. */
export function resetScrollFor(screenId: string): void {
  const parent = findScrollParent(screenElement(screenId));
  if (parent) {
    parent.scrollTop = 0;
    return;
  }
  if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
    window.scrollTo(0, 0);
  }
}

/**
 * Сброс прокрутки при СМЕНЕ экрана (первый показ не трогаем — человек мог
 * прийти по якорю). Выключено — эффект ничего не делает.
 */
export function useScrollResetOnScreenChange(
  enabled: boolean,
  screenId: string | undefined,
): void {
  const prevRef = useRef(screenId);
  useLayoutEffect(() => {
    const prev = prevRef.current;
    prevRef.current = screenId;
    if (!enabled || !screenId || prev === screenId) return;
    resetScrollFor(screenId);
  }, [enabled, screenId]);
}

/** Есть ли что прокручивать у контейнера экрана (или окна). */
export function measureCanScroll(screenId: string): boolean {
  const parent = findScrollParent(screenElement(screenId));
  if (parent) return parent.scrollHeight > parent.clientHeight + 1;
  const doc = document.documentElement;
  return doc.scrollHeight > window.innerHeight + 1;
}

/**
 * Признак «экран прокручивается»: пересчёт на смене экрана (после отрисовки)
 * и на изменении размера окна. Выключено — false и слушателей нет.
 */
export function useScreenCanScroll(
  enabled: boolean,
  screenId: string | undefined,
): boolean {
  const [canScroll, setCanScroll] = useState(false);
  useEffect(() => {
    if (!enabled || !screenId || typeof window === 'undefined') {
      setCanScroll(false);
      return undefined;
    }
    const update = (): void => setCanScroll(measureCanScroll(screenId));
    const frame = window.requestAnimationFrame(update);
    window.addEventListener('resize', update);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', update);
    };
  }, [enabled, screenId]);
  return enabled && canScroll;
}
